import React, { useCallback, useState, useEffect } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

import { Project } from 'services/projects';
import { TextField } from 'ui/components/TextField/TextField';
import { useHandleTextFieldChange } from 'services/forms';
import { Modal } from 'ui/components/Modal/Modal';
import { PermissionType } from 'services/permissions';

interface ProjectModalProps {
  open: boolean;
  project: Project;
  onClose: () => void;
  onSave: (project: Project) => void;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({
  open,
  project,
  onClose,
  onSave,
}) => {
  const [currentProject, setCurrentProject] = useState<Project>(project);
  const [nameError, setNameError] = useState(false);

  useEffect(() => {
    setCurrentProject(project);
    setNameError(false);
  }, [project]);

  const handleTextFieldChange = useHandleTextFieldChange<Project>(
    setCurrentProject,
    currentProject
  );

  const handleSave = useCallback(() => {
    // Validate required fields
    if (!currentProject.name || currentProject.name.trim() === '') {
      setNameError(true);
      return;
    }

    onSave(currentProject);
  }, [currentProject, onSave]);

  const isNewProject = !project.id || project.id === -1;

  return (
    <Modal
      open={open}
      onCancelClicked={onClose}
      onApplyClicked={handleSave}
      title={isNewProject ? 'Add New Project' : 'Edit Project'}
      dataQa="project-modal"
      permissions={[PermissionType.AccountingEdit]}
      applyLabel={isNewProject ? 'Add' : 'Save'}
      cancelLabel="Cancel"
      withoutFooter={false}
    >
      <Box pt={2}>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <TextField
              className={'redesign'}
              variant={'standard'}
              type="text"
              label="Name"
              placeholder="Enter project name"
              name="name"
              required
              autoComplete="nope"
              onChange={handleTextFieldChange}
              value={currentProject.name}
              error={nameError && !currentProject.name}
              helperText={
                nameError && !currentProject.name ? 'Name is required' : ''
              }
              data-qa="project-modal-name"
              permissions={[PermissionType.AccountingEdit]}
            />
          </Grid>
          <Grid xs={12}>
            <TextField
              className={'redesign'}
              variant={'standard'}
              type="text"
              label="Description"
              placeholder="Enter project description"
              name="notes"
              multiline
              rows={4}
              autoComplete="nope"
              onChange={handleTextFieldChange}
              value={currentProject.notes}
              data-qa="project-modal-notes"
              permissions={[PermissionType.AccountingEdit]}
            />
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default ProjectModal;
